/**
 * DeleteAccountModal — web port of iOS DeleteAccountBottomSheet.
 * (client-native/src/features/settings/components/DeleteAccountBottomSheet.tsx)
 *
 * iOS presents a bottom sheet with: a "Delete Account" heading, a bold
 * "Warning!" irreversibility line, the account email in a tap-to-copy chip,
 * an email-confirm input, a red destructive "Delete Account" button and a
 * ghost "Cancel". Web has no bottom sheet, so this renders the same content
 * as a centered modal (bottom-right of the phone column visually) while
 * keeping the exact copy, email-confirm gate and delete logic.
 *
 * The actual delete still goes through deleteUser() (validated by the caller's
 * onDelete), matching the prior web behaviour — only the presentation changed.
 */
import React, { useEffect, useState } from "react";
import { colors } from "@/ui";

interface DeleteAccountModalProps {
  open: boolean;
  onClose: () => void;
  /** The signed-in user's email (the value the confirm input must match). */
  userEmail: string;
  /** Throws if the typed email doesn't match; resolves the auth gate. */
  onAuth: (typed: string) => void;
  /** Performs the actual account deletion. */
  onDelete: () => Promise<void>;
  onTrackDeleteAccountClicked?: () => void;
}

const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({
  open,
  onClose,
  userEmail,
  onAuth,
  onDelete,
  onTrackDeleteAccountClicked,
}) => {
  const [email, setEmail] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // After the email check passes, require a second explicit confirmation before
  // deleting — iOS raises an "Are you sure? This cannot be undone" alert and only
  // deletes from the destructive button (DeleteAccountBottomSheet.tsx). Without
  // it, a matching email deleted the account in a single tap.
  const [confirming, setConfirming] = useState(false);

  // Reset transient state whenever the sheet opens (mirrors iOS present()).
  useEffect(() => {
    if (open) {
      setEmail("");
      setError(null);
      setCopied(false);
      setIsDeleting(false);
      setConfirming(false);
    }
  }, [open]);

  if (!open) return null;

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(userEmail);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard may be unavailable (insecure context) — ignore silently
    }
  };

  const handleDeleteAccount = async () => {
    onTrackDeleteAccountClicked?.();
    try {
      onAuth(email);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Please confirm your account email first.");
      return;
    }
    setError(null);
    // First valid tap: ask for an explicit final confirmation (iOS "Are you
    // sure?" alert). Only the second tap proceeds to delete.
    if (!confirming) {
      setConfirming(true);
      return;
    }
    setIsDeleting(true);
    try {
      await onDelete();
      // onDelete navigates away on success; nothing else to do here.
    } catch (e) {
      setError(e instanceof Error ? e.message : "Please try again later.");
      setIsDeleting(false);
    }
  };

  const fieldStyle: React.CSSProperties = {
    backgroundColor: colors.greyLightest,
    border: `1px solid ${colors.greyLight}`,
    borderRadius: 12,
    padding: "12px 16px",
    width: "100%",
    boxSizing: "border-box",
    fontSize: "var(--base-font-size)",
    color: colors.primary,
    outline: "none",
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Delete Account"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: colors.background,
          borderRadius: 20,
          width: "100%",
          maxWidth: 380,
          padding: 24,
          boxShadow: "0 12px 32px rgba(0,0,0,0.2)",
          boxSizing: "border-box",
        }}
      >
        {/* Header */}
        <div className="text-lg font-bold" style={{ color: colors.primary, marginBottom: 24 }}>
          Delete Account
        </div>

        {/* Warning */}
        <p className="text-base" style={{ color: colors.secondary, marginBottom: 24, lineHeight: 1.4 }}>
          <strong style={{ fontWeight: 700 }}>Warning!</strong> This is irreversible. Your
          subscription will still need to be canceled.
        </p>

        {/* Account email (tap to copy) */}
        <div className="text-sm" style={{ color: colors.secondary, marginBottom: 6 }}>
          Your account email:
        </div>
        <button
          type="button"
          onClick={handleCopyEmail}
          style={{
            ...fieldStyle,
            backgroundColor: colors.white,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
            cursor: "pointer",
            textAlign: "left",
          }}
        >
          <span
            className="text-base font-semibold"
            style={{
              color: colors.primary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {userEmail}
          </span>
          <span className="text-sm" style={{ color: colors.accent, marginLeft: 8, flexShrink: 0 }}>
            {copied ? "Copied" : "Tap to copy"}
          </span>
        </button>

        {/* Confirm input */}
        <div className="text-base font-semibold" style={{ color: colors.primary, marginBottom: 8 }}>
          Enter your email to continue
        </div>
        <input
          value={email}
          onChange={(e) => {
            setEmail(e.target.value.toLowerCase());
            if (error) setError(null);
          }}
          placeholder="Email"
          autoCapitalize="none"
          type="email"
          disabled={isDeleting}
          style={{ ...fieldStyle, backgroundColor: colors.white, marginBottom: error ? 8 : 24 }}
        />

        {error && (
          <p className="text-sm" style={{ color: colors.error, marginBottom: 16 }}>
            {error}
          </p>
        )}

        {/* Final confirmation prompt (iOS "Are you sure?" alert). */}
        {confirming && !isDeleting && (
          <p className="text-sm" style={{ color: colors.error, marginBottom: 16, fontWeight: 600 }}>
            Are you sure? This permanently deletes your account and cannot be undone.
          </p>
        )}

        {/* Destructive action */}
        <button
          type="button"
          onClick={handleDeleteAccount}
          disabled={isDeleting}
          style={{
            width: "100%",
            height: "var(--button-height)",
            borderRadius: 12,
            border: "none",
            backgroundColor: colors.red,
            color: colors.white,
            fontSize: "var(--base-font-size)",
            fontWeight: 600,
            cursor: isDeleting ? "default" : "pointer",
            opacity: isDeleting ? 0.7 : 1,
            marginBottom: 12,
          }}
        >
          {isDeleting
            ? "Deleting..."
            : confirming
            ? "Yes, delete my account"
            : "Delete Account"}
        </button>

        <button
          type="button"
          onClick={onClose}
          disabled={isDeleting}
          style={{
            width: "100%",
            height: "var(--button-height)",
            borderRadius: 12,
            border: "none",
            background: "none",
            color: colors.secondary,
            fontSize: "var(--base-font-size)",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default DeleteAccountModal;
